﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useForm, useWatch, type Control } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  useAddTicketTypeMutation,
  useDeleteTicketTypeMutation,
  useUpdateTicketTypeMutation,
} from "./api";
import { isoToLocalInput, localInputToIso } from "./date-helpers";
import { useMeQuery } from "../organizers/api";

import {
  SUPPORTED_CURRENCIES,
  calculatePriceBreakdown,
  formatCurrency,
  type CurrencyCode,
  type TicketType,
} from "@ticketnjoy/types";

// Boş datetime input değeri → null olarak normalize et.
const emptyToUndefined = z.preprocess(
  (v) => (v === "" || v == null ? undefined : v),
  z.string().optional(),
);

const schema = z
  .object({
    name: z.string().min(1, "İsim zorunlu.").max(100),
    description: z.string().max(1000).optional().or(z.literal("")),
    price: z.coerce.number().min(0, "Fiyat negatif olamaz."),
    currency: z.enum(SUPPORTED_CURRENCIES),
    quantity: z.coerce.number().int().min(1, "Kapasite en az 1 olmalı."),
    salesStart: emptyToUndefined,
    salesEnd: emptyToUndefined,
    minPerOrder: z.coerce.number().int().min(1, "En az 1.").max(100),
    // "" → undefined (limitsiz)
    maxPerOrder: z.preprocess(
      (v) => (v === "" || v == null ? undefined : v),
      z.coerce.number().int().min(1).max(100).optional(),
    ),
    // "" → undefined (event default'unu kullan)
    taxRate: z.preprocess(
      (v) => (v === "" || v == null ? undefined : v),
      z.coerce.number().min(0).max(100).optional(),
    ),
    sortOrder: z.coerce.number().int().default(0),
  })
  .refine(
    (v) =>
      v.salesStart == null ||
      v.salesEnd == null ||
      new Date(v.salesStart) < new Date(v.salesEnd),
    { message: "Satış başlangıcı bitişten önce olmalı.", path: ["salesEnd"] },
  )
  .refine(
    (v) => v.maxPerOrder == null || v.maxPerOrder >= v.minPerOrder,
    { message: "Maksimum minimumdan küçük olamaz.", path: ["maxPerOrder"] },
  );

type FormValues = z.infer<typeof schema>;
type FormInput = z.input<typeof schema>;

const CURRENCY_LABELS: Record<CurrencyCode, string> = {
  TRY: "₺ TRY",
  EUR: "€ EUR",
  USD: "$ USD",
};

/**
 * z.coerce ile form input tipi `unknown` oluyor — HTML Input'a güvenle pass
 * etmek için string/number'a normalize et. null/undefined → "" (controlled boş).
 */
function inputVal(v: unknown): string | number {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return v;
  return String(v);
}

/**
 * Event detay sayfasında kullanılan bilet türü listesi + inline ekleme/düzenleme.
 * Satış başlamışsa Quantity'i Sold altına düşüremez (backend kuralını UI'da da belli et).
 */
export function TicketTypesManager({
  eventSlug,
  ticketTypes,
  eventDefaultTaxRate,
  disabled,
}: {
  eventSlug: string;
  ticketTypes: TicketType[];
  eventDefaultTaxRate: number;
  disabled?: boolean;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: me } = useMeQuery();

  const orgServiceFee = me?.serviceFee ?? 0;
  const orgCommission = me?.commissionRate ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Bilet Türleri</h2>
        {!showAdd && !disabled && (
          <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}>
            <Plus className="mr-1 h-4 w-4" /> Yeni Bilet Türü
          </Button>
        )}
      </div>

      {ticketTypes.length === 0 && !showAdd && (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          Henüz bilet türü yok. Etkinliği yayınlamadan önce en az bir tane eklemelisin.
        </p>
      )}

      <div className="space-y-2">
        {ticketTypes.map((t) =>
          editingId === t.id ? (
            <TicketTypeEditRow
              key={t.id}
              eventSlug={eventSlug}
              ticket={t}
              eventDefaultTaxRate={eventDefaultTaxRate}
              orgServiceFee={orgServiceFee}
              orgCommission={orgCommission}
              onDone={() => setEditingId(null)}
            />
          ) : (
            <TicketTypeRow
              key={t.id}
              eventSlug={eventSlug}
              ticket={t}
              eventDefaultTaxRate={eventDefaultTaxRate}
              orgServiceFee={orgServiceFee}
              orgCommission={orgCommission}
              disabled={disabled}
              onEdit={() => setEditingId(t.id)}
            />
          ),
        )}
      </div>

      {showAdd && (
        <TicketTypeAddRow
          eventSlug={eventSlug}
          eventDefaultTaxRate={eventDefaultTaxRate}
          orgServiceFee={orgServiceFee}
          orgCommission={orgCommission}
          onDone={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}

function TicketTypeRow({
  eventSlug,
  ticket,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
  disabled,
  onEdit,
}: {
  eventSlug: string;
  ticket: TicketType;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
  disabled?: boolean;
  onEdit: () => void;
}) {
  const deleteMutation = useDeleteTicketTypeMutation(eventSlug);

  const taxRate = ticket.taxRate ?? eventDefaultTaxRate;
  const breakdown = calculatePriceBreakdown(
    ticket.price,
    taxRate,
    orgServiceFee,
    orgCommission,
  );

  const handleDelete = () => {
    if (ticket.sold > 0) {
      toast.error("Satış başlamış bilet türü silinemez.");
      return;
    }
    if (!confirm(`"${ticket.name}" silinsin mi?`)) return;
    deleteMutation.mutate(ticket.id, {
      onSuccess: () => toast.success("Bilet türü silindi."),
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <div className="rounded-md border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{ticket.name}</span>
            <Badge variant="secondary" className="font-mono">
              {ticket.currency}
            </Badge>
            {ticket.sold > 0 && (
              <Badge variant="outline">{ticket.sold} satıldı</Badge>
            )}
            {ticket.taxRate != null && (
              <Badge variant="outline">KDV %{ticket.taxRate}</Badge>
            )}
          </div>
          {ticket.description && (
            <p className="break-words text-xs text-muted-foreground">
              {ticket.description}
            </p>
          )}
          <p className="break-words text-sm text-muted-foreground">
            Taban: {formatCurrency(ticket.price, ticket.currency)} → Alıcı:{" "}
            <span className="font-semibold text-foreground">
              {formatCurrency(breakdown.unitGross, ticket.currency)}
            </span>{" "}
            · {ticket.available}/{ticket.quantity} müsait
          </p>
          {(ticket.salesStart || ticket.salesEnd) && (
            <p className="break-words text-xs text-muted-foreground">
              Satış: {formatWindow(ticket.salesStart, ticket.salesEnd)}
            </p>
          )}
        </div>
        {!disabled && (
          <div className="flex shrink-0 gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label="Düzenle">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              aria-label="Sil"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatWindow(start: string | null, end: string | null): string {
  const fmt = (iso: string) =>
    new Intl.DateTimeFormat("tr-TR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  if (start && end) return `${fmt(start)} → ${fmt(end)}`;
  if (start) return `${fmt(start)} sonrası`;
  if (end) return `${fmt(end)}'e kadar`;
  return "";
}

function TicketTypeAddRow({
  eventSlug,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
  onDone,
}: {
  eventSlug: string;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
  onDone: () => void;
}) {
  const mutation = useAddTicketTypeMutation(eventSlug);
  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      currency: "TRY",
      quantity: 100,
      salesStart: "",
      salesEnd: "",
      minPerOrder: 1,
      maxPerOrder: "",
      taxRate: "",
      sortOrder: 0,
    },
  });

  const onSubmit = (values: FormValues) => {
    const payload = {
      name: values.name,
      description: values.description || null,
      price: values.price,
      currency: values.currency,
      quantity: values.quantity,
      salesStart: localInputToIso(values.salesStart ?? null),
      salesEnd: localInputToIso(values.salesEnd ?? null),
      minPerOrder: values.minPerOrder,
      maxPerOrder: values.maxPerOrder ?? null,
      taxRate: values.taxRate ?? null,
      sortOrder: values.sortOrder,
    };
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success("Bilet türü eklendi.");
        form.reset();
        onDone();
      },
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <InlineTicketForm
      form={form}
      title="Yeni Bilet Türü"
      submitLabel="Ekle"
      isSubmitting={mutation.isPending}
      eventDefaultTaxRate={eventDefaultTaxRate}
      orgServiceFee={orgServiceFee}
      orgCommission={orgCommission}
      onSubmit={onSubmit}
      onCancel={onDone}
    />
  );
}

function TicketTypeEditRow({
  eventSlug,
  ticket,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
  onDone,
}: {
  eventSlug: string;
  ticket: TicketType;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
  onDone: () => void;
}) {
  const mutation = useUpdateTicketTypeMutation(eventSlug, ticket.id);
  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: ticket.name,
      description: ticket.description ?? "",
      price: ticket.price,
      currency: ticket.currency,
      quantity: ticket.quantity,
      salesStart: isoToLocalInput(ticket.salesStart),
      salesEnd: isoToLocalInput(ticket.salesEnd),
      minPerOrder: ticket.minPerOrder,
      maxPerOrder: ticket.maxPerOrder ?? "",
      taxRate: ticket.taxRate ?? "",
      sortOrder: ticket.sortOrder,
    },
  });

  const onSubmit = (values: FormValues) => {
    if (values.quantity < ticket.sold) {
      toast.error(`Kapasite satılan (${ticket.sold}) altına düşürülemez.`);
      return;
    }
    const payload = {
      name: values.name,
      description: values.description || null,
      price: values.price,
      currency: values.currency,
      quantity: values.quantity,
      salesStart: localInputToIso(values.salesStart ?? null),
      salesEnd: localInputToIso(values.salesEnd ?? null),
      minPerOrder: values.minPerOrder,
      maxPerOrder: values.maxPerOrder ?? null,
      taxRate: values.taxRate ?? null,
      sortOrder: values.sortOrder,
    };
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success("Güncellendi.");
        onDone();
      },
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <InlineTicketForm
      form={form}
      title={`"${ticket.name}" düzenle`}
      submitLabel="Kaydet"
      isSubmitting={mutation.isPending}
      eventDefaultTaxRate={eventDefaultTaxRate}
      orgServiceFee={orgServiceFee}
      orgCommission={orgCommission}
      onSubmit={onSubmit}
      onCancel={onDone}
    />
  );
}

function InlineTicketForm({
  form,
  title,
  submitLabel,
  isSubmitting,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
  onSubmit,
  onCancel,
}: {
  // RHF + zod `coerce/preprocess` tip köprüsünü basitleştirmek için pragmatik
  // olarak FormInput ile FormValues'u aynı hizaya alıyoruz. Form validasyonu
  // yine `zodResolver(schema)` üzerinden sağlanıyor, handleSubmit FormValues döndürüyor.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: ReturnType<typeof useForm<any, unknown, FormValues>>;
  title: string;
  submitLabel: string;
  isSubmitting: boolean;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
}) {
  return (
    <div className="rounded-md border-2 border-dashed p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel} type="button">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/*
           * 3 anlamlı bölüm:
           *   1) Bilet Bilgileri — kimlik
           *   2) Fiyat & Stok — para/adet/KDV
           *   3) Satış Ayarları — tarih penceresi + sepet limitleri
           *
           * Her bölüm aynı 3-kolonlu grid üzerinde hizalanır (kolon sınırları
           * bölümler arası tutarlı kalsın). Mobilde tek kolon, sm+ üç kolon.
           * `items-start` olmazsa textarea gibi uzun alanlar komşu label'ları
           * aşağı kaydırır.
           */}

          <Section title="Bilet Bilgileri">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>İsim *</FormLabel>
                  <FormControl>
                    <Input placeholder="VIP / Normal / Öğrenci" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sıralama</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} value={inputVal(field.value)} />
                  </FormControl>
                  <FormDescription>Listede küçükten büyüğe</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="sm:col-span-3">
                  <FormLabel>Açıklama</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <Section title="Fiyat & Stok">
            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Taban Fiyat *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>KDV ve servis hariç</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Para Birimi *</FormLabel>
                  <Select
                    value={(field.value as CurrencyCode | undefined) ?? "TRY"}
                    onValueChange={(v) => field.onChange(v as CurrencyCode)}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SUPPORTED_CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {CURRENCY_LABELS[c]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kapasite *</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} value={inputVal(field.value)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="taxRate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>KDV %</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      placeholder={`Varsayılan %${eventDefaultTaxRate}`}
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>Boş → event varsayılanı</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <Section title="Satış Ayarları">
            <FormField
              control={form.control}
              name="salesStart"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Satış Başlangıcı</FormLabel>
                  <FormControl>
                    <Input
                      type="datetime-local"
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>Boş → yayın anında açılır</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="salesEnd"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Satış Bitişi</FormLabel>
                  <FormControl>
                    <Input
                      type="datetime-local"
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>
                    Boş → etkinlik başlangıcında kapanır
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* 3. hücre bilinçli boş: salesEnd ile aynı satırda min/max'in
                kaymaması için kolon ızgarasını koruyoruz. */}
            <div aria-hidden className="hidden sm:block" />
            <FormField
              control={form.control}
              name="minPerOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Min Adet *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>Sepete minimum eklenecek</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="maxPerOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max Adet</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      placeholder="Limitsiz"
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>Tek siparişte üst limit</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <PricePreview
            control={form.control}
            eventDefaultTaxRate={eventDefaultTaxRate}
            orgServiceFee={orgServiceFee}
            orgCommission={orgCommission}
          />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel}>
              İptal
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitLabel}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

/**
 * Canlı fiyat ön-izleme. Organizer panelinde şeffaflık için hem alıcının
 * ödeyeceği brüt hem de platform kesildikten sonra organizer'a kalan net gösterilir.
 */
function PricePreview({
  control,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
}) {
  const values = useWatch({ control }) as Partial<FormInput>;
  const price = Number(values.price) || 0;
  const currency = (values.currency ?? "TRY") as CurrencyCode;
  const taxInput = values.taxRate;
  const effectiveTax =
    taxInput === "" || taxInput == null ? eventDefaultTaxRate : Number(taxInput);

  const breakdown = calculatePriceBreakdown(
    price,
    effectiveTax,
    orgServiceFee,
    orgCommission,
  );

  const organizerNet = price - breakdown.commission;

  return (
    <div className="rounded-md border bg-muted/30 p-3 text-xs">
      <p className="mb-2 font-semibold text-foreground">Fiyat Ön-izleme</p>
      <div className="grid gap-1 sm:grid-cols-2">
        <Row label="Taban" value={formatCurrency(price, currency)} />
        <Row
          label={`KDV (%${effectiveTax})`}
          value={formatCurrency(breakdown.taxAmount, currency)}
        />
        <Row
          label="Servis ücreti"
          value={formatCurrency(orgServiceFee, currency)}
        />
        <Row
          label={`Komisyon (%${orgCommission})`}
          value={`− ${formatCurrency(breakdown.commission, currency)}`}
        />
      </div>
      <div className="mt-2 flex justify-between border-t pt-2 font-semibold text-foreground">
        <span>Alıcı öder</span>
        <span>{formatCurrency(breakdown.unitGross, currency)}</span>
      </div>
      <div className="mt-1 flex justify-between text-muted-foreground">
        <span>Sana kalan (bilet başına)</span>
        <span>{formatCurrency(Math.max(0, organizerNet), currency)}</span>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums text-foreground">{value}</span>
    </div>
  );
}

/**
 * Form'u anlamlı bölümlere ayıran mini kart. Event detay formundaki
 * `VenueFields` / `DateFields` pattern'iyle birebir tutarlı:
 *   `rounded-lg border p-4` + küçük başlık.
 *
 * 3-kolonlu grid bölüm içinde geçerli; mobilde tek kolona düşer.
 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4 rounded-lg border p-4">
      <h4 className="text-sm font-semibold">{title}</h4>
      <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-3">
        {children}
      </div>
    </section>
  );
}
